import api from 'api';
import { BoxWrapper, Button } from 'components/Base';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';
import actions from 'store/actions';
import selectors from 'store/selectors';
import { errHelper, Notify } from 'utils';
import './forecastReturnsConfig.scss';
import EvalueHeaderContent from './EvalueHeaderContent';
import EvalueForecastReturns from './EvalueForecastReturns';
import MorningstarForecastReturns from './MorningstarForecastReturns';

type ForecastReturnsProps = {
  updateMetaReturnForecasts: Function;
  isFirmAdmin: boolean;
  riskProfiler: string;
};

const ForecastReturns: React.FC<ForecastReturnsProps> = ({
  updateMetaReturnForecasts,
  isFirmAdmin,
  riskProfiler
}) => {
  const [riskLevelDescriptions, setRiskLevelDescriptions] = useState([]);
  const [customRiskAndReturn, setCustomRiskAndReturn] = useState({} as any);
  const [defaultRiskAndReturn, setDefaultRiskAndReturn] = useState({} as any);
  const [currentRiskAndReturn, setCurrentRiskAndReturn] =
    useState(customRiskAndReturn);
  const [isCustomForecastReturnEnabled, setIsCustomForecastReturnEnabled] =
    useState(false);
  const [lastEvalueUpdate, setLastEvalueUpdate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const isEvalue = riskProfiler === 'EValue';

  const saveTables = () => {
    setIsSaving(true);

    const method = isEvalue
      ? api.updateReturnForecastsConfig
      : api.updateMorningstarReturnForecastsConfig;

    method(customRiskAndReturn)
      .then(response => {
        updateMetaReturnForecasts(response.data);
        Notify.success(
          'You have updated your Custom Forecast Returns configuration'
        );
        setIsSaving(false);
      })
      .catch(err => {
        setIsSaving(false);
        errHelper(err);
      });
  };

  const toggleIsEnabled = (value: boolean) => {
    setIsCustomForecastReturnEnabled(value);
    api
      .updateEnableForecastsConfig(value)
      .then(() => {
        Notify.success(
          `Custom Forecast Returns have been ${value ? 'enabled' : 'disabled'}`
        );
      })
      .catch(err => errHelper(err));
  };

  const reset = () => {
    setIsResetting(true);

    const method = isEvalue
      ? api.resetReturnForecastsConfig()
      : api.resetMorningstarReturnForecastsConfig();

    method
      .then(response => {
        setCustomRiskAndReturn(response.data.config[0]);
        setIsCustomForecastReturnEnabled(response.data.isEnabled);
        setDefaultRiskAndReturn(response.data.config[1]);
        Notify.success(`Custom Forecast Returns have been reset to default`);
        setIsResetting(false);
      })
      .catch(err => {
        setIsResetting(false);
        errHelper(err);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      api.getRiskDescriptions(),
      isEvalue
        ? api.getMetaReturnForecastsConfig()
        : api.getMorningstarReturnForecastsConfig()
    ]).then(([riskDescriptionData, forecastReturnsData]) => {
      setRiskLevelDescriptions(riskDescriptionData.data);
      setCustomRiskAndReturn(forecastReturnsData.data.config[0]);
      setIsCustomForecastReturnEnabled(forecastReturnsData.data.isEnabled);
      setDefaultRiskAndReturn(forecastReturnsData.data.config[1]);
      setLastEvalueUpdate(forecastReturnsData.data.most_recent_evalue_update);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setCurrentRiskAndReturn(
      isCustomForecastReturnEnabled ? customRiskAndReturn : defaultRiskAndReturn
    );
  }, [
    isCustomForecastReturnEnabled,
    customRiskAndReturn,
    defaultRiskAndReturn
  ]);

  return (
    <div>
      <BoxWrapper
        title="Forecast Investment Returns"
        subTitle="Customise the forecast investment returns used in Fintegrate for each risk profiler and investment time horizon. By default, these returns are set by EV. This setting allows you to adjust the projections to align with your firm's expectations and market outlook. Note: Changes made here will apply across your firm."
      >
        {isLoading ? (
          <LoaderSpinner isLoading={isLoading} />
        ) : (
          <>
            <div className="forcastButtons">
              {isFirmAdmin && (
                <div className="forecastToggleContainer">
                  <h4>Custom Forecast Returns Enabled</h4>
                  <ToggleSwitch
                    options={[
                      { text: 'Enabled', value: true },
                      { text: 'Disabled', value: false }
                    ]}
                    isTrue={isCustomForecastReturnEnabled}
                    toggleIsEnabled={toggleIsEnabled}
                  />
                </div>
              )}
              {isFirmAdmin && isCustomForecastReturnEnabled && (
                <div>
                  <h4>Reset to defaults</h4>
                  <Button
                    isLoading={isResetting}
                    color="primary"
                    type="submit"
                    onClick={() => reset()}
                  >
                    Reset
                  </Button>
                </div>
              )}
              {isEvalue && (
                <EvalueHeaderContent lastEvalueUpdate={lastEvalueUpdate} />
              )}
            </div>
            {isEvalue ? (
              <EvalueForecastReturns
                currentRiskAndReturn={currentRiskAndReturn}
                defaultRiskAndReturn={defaultRiskAndReturn}
                riskLevelDescriptions={riskLevelDescriptions}
                customRiskAndReturn={customRiskAndReturn}
                setValues={setCustomRiskAndReturn}
                saveTables={saveTables}
                isSaving={isSaving}
                isFirmAdmin={isFirmAdmin}
                isCustom={isCustomForecastReturnEnabled}
              />
            ) : (
              <MorningstarForecastReturns
                currentRiskAndReturn={currentRiskAndReturn}
                defaultRiskAndReturn={defaultRiskAndReturn}
                customRiskAndReturn={customRiskAndReturn}
                setValues={setCustomRiskAndReturn}
                saveTables={saveTables}
                isSaving={isSaving}
                isFirmAdmin={isFirmAdmin}
                isCustom={isCustomForecastReturnEnabled}
              />
            )}
          </>
        )}
      </BoxWrapper>
    </div>
  );
};

export default compose(
  connect(
    (state: any) => ({
      riskProfiler: selectors.firmSelectors.riskProfiler(state)
    }),
    {
      updateMetaReturnForecasts: actions.metaActions.updateMetaReturnForecasts
    }
  )
)(ForecastReturns);
